import React, { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Switch from "@mui/material/Switch";
import ColorPicker from "react-pick-color";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import FlagIcon from "@mui/icons-material/Flag";
import { RATING_COLOR_PRESETS, hexToRgb, rgbToHex } from "./ratingColorUtils";
import "../../../../assets/styles/containers/_editGlobalRating.scss";

// standalone=true hides the per-template "Default rating" toggle — used e.g. from the Ratings
// tab, where there's no single template to mark a default rating for.
function EditGlobalRating({
    open,
    onClose,
    ratingName,
    setRatingName,
    definition,
    setDefinition,
    ratingColor,
    setRatingColor,
    newRatingSummary,
    setNewRatingSummary,
    isDefaultTemplate,
    setIsDefaultTemplate,
    onUpdate,
    onSaveAsNew,
    ratingNameError,
    standalone = false
}) {
    const includeInSummary = parseInt(newRatingSummary) === 1;

    const [hexInput, setHexInput] = useState((ratingColor || "").replace("#", ""));
    const rgb = useMemo(() => hexToRgb(ratingColor), [ratingColor]);

    useEffect(() => {
        setHexInput((ratingColor || "").replace("#", "").toUpperCase());
    }, [ratingColor]);

    const handleHexInputChange = (raw) => {
        const digits = raw.replace(/[^0-9A-Fa-f]/g, "").slice(0, 6).toUpperCase();
        setHexInput(digits);
        if (digits.length === 6) setRatingColor(`#${digits}`);
    };

    const handleRgbChannelChange = (channel, value) => {
        const next = { ...rgb, [channel]: Math.max(0, Math.min(255, parseInt(value, 10) || 0)) };
        setRatingColor(rgbToHex(next.r, next.g, next.b));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="edit-rating-modal-container">
            <div className="edit-rating-modal-header">
                <Typography className="edit-rating-modal-title">Edit rating</Typography>
                <CloseOutlinedIcon className="edit-rating-modal-close" onClick={onClose} />
            </div>
            <DialogContent className="edit-rating-modal-content">
                <div className="edit-rating-fields-col">
                    <div className="edit-rating-preview">
                        <span className="edit-rating-preview-label">Preview</span>
                        <div className="edit-rating-preview-content">
                            <span className="edit-rating-preview-swatch" style={{ background: ratingColor }}>
                                {includeInSummary && <FlagIcon />}
                            </span>
                            <span className="edit-rating-preview-name">{ratingName || "Rating name"}</span>
                        </div>
                    </div>
                    <div className="edit-rating-field">
                        <label className="edit-rating-label">Name</label>
                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            className="edit-rating-input"
                            value={ratingName}
                            onChange={(e) => setRatingName(e.target.value)}
                            error={!!ratingNameError}
                            helperText={ratingNameError && "Rating name is required"}
                        />
                    </div>
                    <div className="edit-rating-field">
                        <label className="edit-rating-label">Definition</label>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            variant="outlined"
                            className="edit-rating-input"
                            value={definition}
                            onChange={(e) => setDefinition(e.target.value)}
                        />
                    </div>
                    {!standalone && (
                        <div className="edit-rating-toggle-section">
                            <div className="edit-rating-toggle-row">
                                <Switch checked={!!isDefaultTemplate} onChange={(e) => setIsDefaultTemplate(e.target.checked)} />
                                <span className="edit-rating-toggle-label">Default rating</span>
                            </div>
                            <p className="edit-rating-toggle-helper">
                                This rating will be automatically assigned to all the lines and comments in this template. Only one rating can be the default per template.
                            </p>
                        </div>
                    )}
                    <div className="edit-rating-toggle-section">
                        <div className="edit-rating-toggle-row">
                            <Switch
                                checked={includeInSummary}
                                onChange={(e) => setNewRatingSummary(e.target.checked ? "1" : "0")}
                            />
                            <span className="edit-rating-toggle-label">Include in report summary</span>
                        </div>
                        <p className="edit-rating-toggle-helper">
                            Any comment or line with this rating assigned will be included in the report summary.
                        </p>
                    </div>
                </div>
                <div className="edit-rating-color-col">
                    <ColorPicker
                        hideAlpha
                        hideInputs
                        color={ratingColor}
                        className="rating-color-picker"
                        onChange={(color) => setRatingColor(color.hex)}
                        theme={{ background: "transparent", borderColor: "transparent", boxShadow: "none"}}

                    />
                    <div className="rating-color-fields">
                        <div className="rating-color-field">
                            <label className="rating-color-field-label">Hex</label>
                            <div className="rating-color-field-input rating-color-field-hex">
                                <span className="rating-color-field-prefix">#</span>
                                <input
                                    type="text"
                                    maxLength={6}
                                    value={hexInput}
                                    onChange={(e) => handleHexInputChange(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="rating-color-field">
                            <label className="rating-color-field-label">R</label>
                            <div className="rating-color-field-input">
                                <input
                                    type="number"
                                    min={0}
                                    max={255}
                                    value={rgb.r}
                                    onChange={(e) => handleRgbChannelChange("r", e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="rating-color-field">
                            <label className="rating-color-field-label">G</label>
                            <div className="rating-color-field-input">
                                <input
                                    type="number"
                                    min={0}
                                    max={255}
                                    value={rgb.g}
                                    onChange={(e) => handleRgbChannelChange("g", e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="rating-color-field">
                            <label className="rating-color-field-label">B</label>
                            <div className="rating-color-field-input">
                                <input
                                    type="number"
                                    min={0}
                                    max={255}
                                    value={rgb.b}
                                    onChange={(e) => handleRgbChannelChange("b", e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="rating-color-presets">
                        {RATING_COLOR_PRESETS.map((presetColor) => (
                            <button
                                key={presetColor}
                                type="button"
                                className="rating-color-preset-swatch"
                                style={{ background: presetColor }}
                                onClick={() => setRatingColor(presetColor)}
                                aria-label={presetColor}
                            />
                        ))}
                    </div>
                </div>
            </DialogContent>
            <DialogActions className="edit-rating-modal-actions">
                <Button className="ratings-cancel-btn" onClick={onClose}>
                    Cancel
                </Button>
                <div className="edit-rating-actions-spacer" />
                {onSaveAsNew && (
                    <Button className="edit-rating-save-new-btn" onClick={() => onSaveAsNew(ratingName, definition)}>
                        Save as new
                    </Button>
                )}
                <Button className="ratings-submit-btn" onClick={() => onUpdate(ratingName, definition)}>
                    Update global rating
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default EditGlobalRating;